// contato.js — Canais oficiais de contato da editora, do lado da loja.
//
// POR QUE ESTE ARQUIVO EXISTE:
// o telefone aparecia como literal em cada ponto que precisava dele, e nas DUAS
// formas do mesmo número: o E.164 que vai no link (`5584992293892`) e o
// formatado que a pessoa lê na tela. Duas cópias independentes do mesmo dado é
// exatamente como se cria a situação que o cliente reportou — a faixa laranja do
// cabeçalho anunciava `(84) 9229-3892`, um dígito a menos que o número para onde
// o link do CONTATO, na mesma tela, levava. O erro nasceu junto com o Header e
// sobreviveu porque nada no código ligava as duas escritas.
//
// A regra daqui: existe UM número, em E.164, e a forma lida é DERIVADA dele por
// função. O formatado não pode mais divergir do link porque deixou de ser uma
// cópia — é o mesmo dado com máscara.
//
// O custo: este arquivo é a fonte única DA LOJA, não do monorepo. A API, o
// painel e a landing têm as suas próprias cópias do número; trocar o telefone
// da editora continua sendo trabalho em quatro projetos. O que este arquivo
// elimina é a divergência DENTRO da loja, que é onde o defeito apareceu.

/**
 * O número da editora em E.164, só dígitos (55 + DDD + assinante).
 * É esta string que manda: tudo abaixo é derivado dela.
 */
export const TELEFONE_E164 = "5584992293892";

/**
 * "5584992293892" -> "(84) 99229-3892".
 *
 * O corte dos últimos quatro dígitos conta a partir do FIM de propósito: assim o
 * mesmo formatador serve o celular de 9 dígitos e o fixo de 8 sem precisar de
 * uma segunda regra e sem ninguém ter que lembrar qual das duas usar.
 *
 * Número curto ou estranho sai sem máscara em vez de sair mascarado errado:
 * um número visivelmente cru na tela é um bug que alguém conserta; um número
 * bem formatado e errado é o bug que estamos consertando agora.
 */
export const formatarTelefoneBR = (e164) => {
  const digitos = String(e164 || "").replace(/\D/g, "");
  const nacional = digitos.startsWith("55") ? digitos.slice(2) : digitos;
  const ddd = nacional.slice(0, 2);
  const assinante = nacional.slice(2);
  if (ddd.length < 2 || assinante.length < 8) return digitos;
  const corte = assinante.length - 4;
  return `(${ddd}) ${assinante.slice(0, corte)}-${assinante.slice(corte)}`;
};

/** "(84) 99229-3892" — o que vai na tela. Nunca escreva isto à mão. */
export const TELEFONE_TEXTO = formatarTelefoneBR(TELEFONE_E164);

/** `tel:` para quem está no celular tocar e ligar. */
export const TELEFONE_HREF = `tel:+${TELEFONE_E164}`;

/**
 * Conversa direta no WhatsApp. `wa.me` é a forma curta oficial do click-to-chat
 * e cai no mesmo lugar que `api.whatsapp.com/send?phone=` — o rodapé usava a
 * segunda forma, o cabeçalho a primeira, e as duas abriam a mesma conversa.
 * Ficou a curta, porque é a que se lê sem interpretar query string.
 */
export const WHATSAPP_HREF = `https://wa.me/${TELEFONE_E164}`;

/**
 * O CTA de "publique seu livro" NÃO usa o número acima: usa um encurtador
 * cadastrado fora do código. Ele está aqui para ficar num lugar só, não porque
 * seja derivável — não dá para saber pelo repositório para qual número ele
 * redireciona hoje. Se um dia a editora trocar de linha, este link precisa ser
 * conferido no painel do encurtador; trocar `TELEFONE_E164` não o alcança.
 */
export const WHATSAPP_PUBLICAR_HREF = "https://wa.link/o4jzw4";

/** O e-mail que a seção de contato mostra ao visitante. */
export const EMAIL_CONTATO = "insigneacademicaeditora@gmail.com";
export const EMAIL_CONTATO_HREF = `mailto:${EMAIL_CONTATO}`;

/**
 * O e-mail da editora é UM só desde 07/10/2026: o dono confirmou que o
 * endereço da Insigne é insigneacademicaeditora@gmail.com. O rodapé continua
 * com a constante própria porque leva um corpo de mensagem já preenchido
 * sobre publicar.
 */
export const EMAIL_PUBLICAR = EMAIL_CONTATO;
export const EMAIL_PUBLICAR_HREF =
  `mailto:${EMAIL_PUBLICAR}?body=%20Ol%C3%A1%2C%20Editor%20Pedro!` +
  "%20Tenho%20interesse%20em%20publicar%20meu%20texto%20como%20livro%20pela%20Insigne%20Acad%C3%AAmica%20Editora!";
